import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const MAX_RECONNECT_ATTEMPTS = 5;

export const useSSE = (onEvent) => {
    const { token } = useAuth();
    const [isConnected, setIsConnected] = useState(false);
    const [lastEvent, setLastEvent] = useState(null);
    const eventSourceRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttempts = useRef(0);

    // Keep the latest handler in a ref so a reconnect is not needed every time
    // the caller's callback identity changes - otherwise events would be handled
    // by a stale closure (e.g. refetching with outdated filters).
    const onEventRef = useRef(onEvent);
    useEffect(() => {
        onEventRef.current = onEvent;
    }, [onEvent]);

    const disconnect = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }

        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        setIsConnected(false);
    }, []);

    const connect = useCallback(() => {
        if (!token || eventSourceRef.current) return;

        try {
            // EventSource cannot send an Authorization header, so the token goes
            // in the query string - /api/sse accepts it there.
            const eventSource = new EventSource(
                `/api/sse/events?token=${encodeURIComponent(token)}`
            );
            eventSourceRef.current = eventSource;

            eventSource.onopen = () => {
                console.log('SSE: Connected');
                setIsConnected(true);
                reconnectAttempts.current = 0;
            };

            eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);

                    if (data.type === 'heartbeat') return;

                    setLastEvent(data);

                    if (onEventRef.current) {
                        onEventRef.current(data);
                    }
                } catch (error) {
                    console.error('SSE: Error parsing event data:', error);
                }
            };

            eventSource.onerror = () => {
                setIsConnected(false);
                eventSource.close();
                eventSourceRef.current = null;

                if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
                    console.log('SSE: Max reconnection attempts reached');
                    return;
                }

                // Reconnect with exponential backoff
                const backoffDelay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
                reconnectAttempts.current++;

                console.log(`SSE: Reconnecting in ${backoffDelay}ms (attempt ${reconnectAttempts.current})`);

                reconnectTimeoutRef.current = setTimeout(connect, backoffDelay);
            };
        } catch (error) {
            console.error('SSE: Failed to create EventSource:', error);
        }
    }, [token]);

    const retry = useCallback(() => {
        disconnect();
        reconnectAttempts.current = 0;
        connect();
    }, [connect, disconnect]);

    useEffect(() => {
        connect();

        return () => {
            disconnect();
        };
    }, [connect, disconnect]);

    // Drop the connection while the tab is hidden, restore it when visible again
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                disconnect();
            } else {
                reconnectAttempts.current = 0;
                connect();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [connect, disconnect]);

    return {
        isConnected,
        lastEvent,
        retry,
        disconnect
    };
};
